// Stage 90: Parallax Module — Settings tab.
//
// Operator-only. Renders:
//   - Enable switch + the D-Project explainer (matter-of-fact, no banner).
//   - Healthcheck banner.
//   - Mode radio (cup / trajectory).
//   - Trajectory window + threshold tuners.
//   - "Archive raw cup-reads to R2 (research only)" opt-in.
//   - The live current-slot Parallax inspector pane.
//
// NO history, NO regenerate, NO force-re-read, NO export — Parallax is
// event-driven by mood-region transitions, with the slot living in KV
// volatile-by-TTL (TINS §Implementation Notes #6, #8).

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import {
  parallaxHealthcheck,
  type ParallaxHealth,
} from '../api/parallax';
import { ParallaxInspector } from './ParallaxInspector';

interface SettingsResponse {
  settings: Record<string, string>;
}

const THRESHOLD_KEYS: ReadonlyArray<
  readonly [string, string, string, 'text' | 'number']
> = [
  [
    'parallax_trajectory_window_turns',
    'trajectory window (assistant turns; min 2)',
    '8',
    'number',
  ],
  ['parallax_temperature', 'temperature (hard cap 0.6)', '0.4', 'number'],
  ['parallax_max_output_tokens_cup', 'max output tokens (cup mode)', '100', 'number'],
  [
    'parallax_max_output_tokens_trajectory',
    'max output tokens (trajectory mode)',
    '250',
    'number',
  ],
  ['parallax_timeout_ms_cup', 'timeout (ms, cup mode)', '4000', 'number'],
  [
    'parallax_timeout_ms_trajectory',
    'timeout (ms, trajectory mode)',
    '6000',
    'number',
  ],
  ['parallax_max_anchors', 'max anchors per cup read', '8', 'number'],
  ['parallax_slot_ttl_seconds', 'KV slot TTL (seconds; default 6 h)', '21600', 'number'],
];

export function ParallaxTab({
  onChanged,
  currentConversationId,
}: {
  onChanged: () => void;
  currentConversationId?: string | null;
}) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [mode, setMode] = useState<'cup' | 'trajectory'>('cup');
  const [archiveOn, setArchiveOn] = useState<boolean>(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [health, setHealth] = useState<ParallaxHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    setErr(null);
    try {
      const res = await apiGet<SettingsResponse>('/api/settings');
      const s = res.settings ?? {};
      setSettings(s);
      setEnabled(s.parallax_enabled === 'true');
      setMode(s.parallax_mode === 'trajectory' ? 'trajectory' : 'cup');
      setArchiveOn(s.parallax_archive_raw_reads === 'true');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load settings');
    }
    try {
      const h = await parallaxHealthcheck();
      setHealth(h);
      setHealthErr(null);
    } catch (e) {
      setHealthErr(e instanceof Error ? e.message : 'healthcheck failed');
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const writeSetting = useCallback(
    async (key: string, value: string) => {
      setErr(null);
      setInfo(null);
      try {
        await apiJson('PUT', '/api/settings', { [key]: value });
        setSettings((prev) => ({ ...prev, [key]: value }));
        onChanged();
      } catch (e) {
        setErr(e instanceof Error ? e.message : `failed to write ${key}`);
      }
    },
    [onChanged],
  );

  const toggleEnabled = useCallback(async () => {
    if (enabled === null) return;
    setBusy(true);
    const next = !enabled;
    await writeSetting('parallax_enabled', next ? 'true' : 'false');
    setEnabled(next);
    setInfo(next ? 'Parallax enabled.' : 'Parallax disabled.');
    setBusy(false);
    try {
      const h = await parallaxHealthcheck();
      setHealth(h);
    } catch {
      /* ignore */
    }
  }, [enabled, writeSetting]);

  const changeMode = useCallback(
    async (next: 'cup' | 'trajectory') => {
      setMode(next);
      await writeSetting('parallax_mode', next);
    },
    [writeSetting],
  );

  const toggleArchive = useCallback(async () => {
    const next = !archiveOn;
    setArchiveOn(next);
    await writeSetting('parallax_archive_raw_reads', next ? 'true' : 'false');
  }, [archiveOn, writeSetting]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h3 style={{ margin: 0 }}>Parallax</h3>
        <p
          style={{
            margin: '6px 0 0 0',
            color: 'var(--muted)',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          The sixth pillar. Cup-as-proprioception: on a mood-region
          transition, a single low-temperature inference reads the
          persona&rsquo;s recent trajectory against load-bearing commitment
          anchors derived from the current mood pool. The result lands as a{' '}
          <code>PARALLAX READOUT</code> block in the system prompt of the{' '}
          <em>next</em> turn. Parallax does not steer — it perceives.
          Activating this pillar on a fully-populated five-pillar SYNTH-WEB
          instance constitutes the D Project.
        </p>
      </header>

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={enabled === true}
            onChange={toggleEnabled}
            disabled={enabled === null || busy}
          />
          <strong>Enable Parallax</strong>
        </label>
        {health ? (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>
            Healthcheck: <code>{health.service}</code> · model{' '}
            <code>{health.model}</code> · API key{' '}
            {health.apiKeyConfigured ? '✓ configured' : '⚠ not set'} ·
            region <code>{health.region}</code>
          </div>
        ) : healthErr ? (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>
            Healthcheck failed: {healthErr}
          </div>
        ) : null}
      </section>

      {err ? (
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>
      ) : null}
      {info ? (
        <div style={{ color: 'var(--ok)', fontSize: 13 }}>{info}</div>
      ) : null}

      <section>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Mode</h4>
        <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
          >
            <input
              type="radio"
              name="parallax-mode"
              value="cup"
              checked={mode === 'cup'}
              onChange={() => void changeMode('cup')}
              disabled={enabled !== true}
            />
            cup
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>
              (default — single tagged line)
            </span>
          </label>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
          >
            <input
              type="radio"
              name="parallax-mode"
              value="trajectory"
              checked={mode === 'trajectory'}
              onChange={() => void changeMode('trajectory')}
              disabled={enabled !== true}
            />
            trajectory
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>
              (adds an arc summary line)
            </span>
          </label>
        </div>
      </section>

      <section>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Thresholds</h4>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(260px, 1fr) 160px',
            gap: 8,
            alignItems: 'center',
            fontSize: 13,
          }}
        >
          {THRESHOLD_KEYS.map(([key, label, fallback, kind]) => (
            <ThresholdRow
              key={key}
              k={key}
              label={label}
              value={settings[key] ?? fallback}
              kind={kind}
              onWrite={(v) => writeSetting(key, v)}
            />
          ))}
        </div>
      </section>

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderLeft: '3px solid var(--warning)',
          borderRadius: 8,
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={archiveOn}
            onChange={toggleArchive}
            disabled={enabled !== true}
          />
          <strong style={{ color: 'var(--warning)' }}>
            Archive raw cup-reads to R2 (research only)
          </strong>
        </label>
        <p style={{ margin: '6px 0 0 0', fontSize: 12, color: 'var(--muted)' }}>
          When enabled, the raw cup-read markdown is uploaded to R2 under{' '}
          <code>parallax-archive/&#123;conversationId&#125;/...md</code> for
          30 days. Off by default. The retention rule is configured on the R2
          bucket, not in code.
        </p>
      </section>

      <section>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
          Live current cup
        </h4>
        <ParallaxInspector currentConversationId={currentConversationId} />
      </section>
    </div>
  );
}

function ThresholdRow({
  k,
  label,
  value,
  kind,
  onWrite,
}: {
  k: string;
  label: string;
  value: string;
  kind: 'text' | 'number';
  onWrite: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => {
    setLocal(value);
  }, [value]);
  return (
    <>
      <label htmlFor={`parallax-${k}`} style={{ color: 'var(--muted)' }}>
        {label}
      </label>
      <input
        id={`parallax-${k}`}
        type={kind}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local !== value) onWrite(local);
        }}
        style={{
          padding: '4px 8px',
          fontFamily: 'monospace',
          fontSize: 12,
        }}
      />
    </>
  );
}
